import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import Runsafari from "@/pages/Runsafari";
import Destinations from "@/pages/Destinations";
import SportsTourism from "@/pages/SportsTourism";
import Partners from "@/pages/Partners";
import GalleryPage from "@/pages/GalleryPage";
import Stories from "@/pages/Stories";
import StoryDetail from "@/pages/StoryDetail";
import Results from "@/pages/Results";
import Contact from "@/pages/Contact";
import Faq from "@/pages/Faq";
import Privacy from "@/pages/Privacy";
import GiveBack from "@/pages/GiveBack";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import { ForgotPassword, ResetPassword } from "@/pages/admin/AdminAuth";
import "@/App.css";

const PAGE_TITLES = {
  "/": "SPORTIVE — Sports Tourism • Marathons • Offbeat Destinations India",
  "/about": "About SPORTIVE — Sports Tourism Company, Kolkata India",
  "/events": "Upcoming Marathons & Running Events in India — SPORTIVE",
  "/runsafari": "RUNSAFARI — Destination Running Experiences | SPORTIVE",
  "/destinations": "Offbeat Running Destinations in India — SPORTIVE",
  "/sports-tourism": "Sports Tourism, Impact & Sustainability — SPORTIVE",
  "/partners": "Partnership & Sponsorship Opportunities — SPORTIVE",
  "/gallery": "Event Gallery — SPORTIVE",
  "/stories": "SPORTIVE Stories — Running & Destination News",
  "/results": "Race Results, Certificates & My Registration — SPORTIVE",
  "/contact": "Contact SPORTIVE — Marathons & Sports Tourism India",
  "/faq": "FAQs — SPORTIVE Events & RUNSAFARI",
  "/give-back": "Donation — Run For A Cause | SPORTIVE",
  "/donation": "Donation — Run For A Cause | SPORTIVE",
  "/privacy": "Privacy Policy — SPORTIVE",
  "/admin/login": "Admin Login — SPORTIVE",
  "/admin": "Admin CMS — SPORTIVE",
};

function TitleManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    const p = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
    const t = PAGE_TITLES[p]
      || (p.startsWith("/events/") ? "Event Details & Registration — SPORTIVE"
        : p.startsWith("/stories/") ? "SPORTIVE Stories" : "SPORTIVE — RUN • EXPLORE • CONNECT");
    document.title = t;
  }, [pathname]);
  return null;
}

function PublicLayout({ children }) {
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
    </>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-brand-ink font-mono text-xs tracking-[0.3em] text-brand-cyan">LOADING...</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <TitleManager />
        <Toaster theme="dark" richColors position="top-right" />
        <Routes>
          <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
          <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
          <Route path="/events" element={<PublicLayout><Events /></PublicLayout>} />
          <Route path="/events/:slug" element={<PublicLayout><EventDetail /></PublicLayout>} />
          <Route path="/runsafari" element={<PublicLayout><Runsafari /></PublicLayout>} />
          <Route path="/destinations" element={<PublicLayout><Destinations /></PublicLayout>} />
          <Route path="/sports-tourism" element={<PublicLayout><SportsTourism /></PublicLayout>} />
          <Route path="/partners" element={<PublicLayout><Partners /></PublicLayout>} />
          <Route path="/gallery" element={<PublicLayout><GalleryPage /></PublicLayout>} />
          <Route path="/stories" element={<PublicLayout><Stories /></PublicLayout>} />
          <Route path="/stories/:slug" element={<PublicLayout><StoryDetail /></PublicLayout>} />
          <Route path="/results" element={<PublicLayout><Results /></PublicLayout>} />
          <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
          <Route path="/faq" element={<PublicLayout><Faq /></PublicLayout>} />
          <Route path="/privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
          <Route path="/donation" element={<PublicLayout><GiveBack /></PublicLayout>} />
          <Route path="/give-back" element={<Navigate to="/donation" replace />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/admin" element={<Protected><AdminDashboard /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
